import {forwardRef, useEffect, useRef, useState} from 'react';
import {m, useReducedMotion} from 'motion/react';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';

import abbVieLogo from '@/assets/images/home/trustedBrands/abb-vie.svg';
import adidasLogo from '@/assets/images/home/trustedBrands/adidas.svg';
import fedexLogo from '@/assets/images/home/trustedBrands/fedex.svg';
import georgetownLogo from '@/assets/images/home/trustedBrands/georgetown-university.svg';
import googleLogo from '@/assets/images/home/trustedBrands/google.svg';
import proctorGambleLogo from '@/assets/images/home/trustedBrands/proctor-gamble.svg';
import verizonLogo from '@/assets/images/home/trustedBrands/verizon.svg';

const BRAND_LOGOS = [
  googleLogo,
  proctorGambleLogo,
  abbVieLogo,
  fedexLogo,
  georgetownLogo,
  adidasLogo,
  verizonLogo,
];

const BrandsBar = forwardRef<HTMLElement, unknown>(function BrandsBar(_props, ref) {
  const {t} = useTranslation('home');
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.scrollWidth / 2);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  return (
    <section ref={ref} className="pb-8 pt-16">
      <div className="mx-auto">
        <h2 className="text-center text-lg font-bold uppercase leading-[38px] text-peppercorn-500">
          {t('brandsBar.title')}
        </h2>
        <div className="relative mx-auto mt-6 max-w-[1200px] overflow-hidden before:pointer-events-none before:absolute before:left-0 before:top-0 before:z-10 before:h-full before:w-16 before:bg-gradient-to-r before:from-white before:to-transparent after:pointer-events-none after:absolute after:right-0 after:top-0 after:z-10 after:h-full after:w-16 after:bg-gradient-to-l after:from-white after:to-transparent">
          <div className="flex">
            <m.div
              ref={containerRef}
              className="space-between flex flex-nowrap items-center gap-24 px-16"
              animate={{
                x: containerWidth > 0 && !prefersReducedMotion ? [0, -containerWidth + 16] : 0,
              }}
              transition={{
                x: {
                  repeat: Infinity,
                  repeatType: 'loop',
                  duration: 30,
                  ease: 'linear',
                },
              }}
            >
              {BRAND_LOGOS.map((logo, index) => (
                <Image alt="" src={logo} className="flex-shrink-0" key={index} />
              ))}
              {BRAND_LOGOS.map((logo, index) => (
                <Image alt="" src={logo} className="flex-shrink-0" key={`duplicate-${index}`} />
              ))}
            </m.div>
          </div>
        </div>
      </div>
    </section>
  );
});

export default BrandsBar;
